








































































































































































import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { emailApi } from '@/features/settings/services/emailApiService';
import Modal from '@/shared/components/Modal.vue';
import { EmailTemplateViewModel } from '@/features/settings/models/emailTemplateViewModel';
import TitledSection from '@/shared/components/TitledSection.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import Toast from '@/shared/services/toast';
import { maxLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import { EmailWorkspaceModel } from '@/features/settings/models/response/emailWorkspaceModel';
import Badge from '@/shared/components/Badge.vue';

const NULL_TEMPLATE: EmailTemplateViewModel = {  // Null Object pattern
	templateId: '',
	name: 'NULL'
};

@Component({
	components: {
		LoadingSpinner,
		TitledSection,
		Modal,
		Badge
	},
	mixins: [validationMixin],
	validations: {
		activeTemplateId: {},
		domain: {maxLength: maxLength(67)}
	}
})
export default class Email extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;

	private isLoading: boolean = true;
	private updateInProgress: boolean = false;
	private verifyInProgress: boolean = false;

	private showDeleteTemplateModal: boolean = false;
	private deleteTemplateModel: EmailTemplateViewModel;

	private activeTemplateId: string = '';
	private lastActiveTemplateId: string = '';
	private domain: string = '';
	private domainVerified: boolean = false;
	private domainSaved: boolean = false;
	private templates: EmailTemplateViewModel[] = [];

	// should reflect what is currently stored on the server, only change with repsonses from the server
	private savedEmailWorkspace: EmailWorkspaceModel;

	public async mounted() {
		try {
			await this.loadTemplates();
		} catch (error) {
			if (error.status !== 404) {
				this.handleSeriousError(error.message);
			}
		} finally {
			this.isLoading = false;
		}
	}

	private async loadTemplates() {
		this.templates = await emailApi.getTemplates(this.currentWorkspace.id);
		await this.loadConfiguration();
	}

	private async loadConfiguration() {
		const response: EmailWorkspaceModel = await emailApi.getEmailWorkspace(this.currentWorkspace.id);
		this.setConfig(response);
	}

	private setConfig(emailWorkspace: EmailWorkspaceModel) {
		this.savedEmailWorkspace = emailWorkspace;

		this.domain = emailWorkspace.outboundEmailDomain;
		this.domainVerified = emailWorkspace.outboundEmailDomainVerified;
		this.domainSaved = !!this.domain;

		this.activeTemplateId = emailWorkspace.activeTemplateId;
		this.lastActiveTemplateId = emailWorkspace.activeTemplateId;
	}

	private get hasTemplates(): boolean {
		return this.templates.length > 0;
	}

	private getStatus(template: EmailTemplateViewModel): string {
		const templateActive = template.templateId === this.lastActiveTemplateId;
		if (templateActive) {
			return 'Active';
		} else {
			return 'Inactive';
		}
	}

	private getStatusClass(template: EmailTemplateViewModel): string {
		const templateActive = template.templateId === this.lastActiveTemplateId;
		if (templateActive) {
			return 'success';
		} else {
			return 'disabled';
		}
	}

	private openTemplateEditorForCreate() {
		this.$router.push({name: 'email-template-create', params: {}});
	}

	private editTemplate(template: EmailTemplateViewModel) {
		this.$router.push({
			name: 'email-template-edit',
			params: {templateId: template.templateId, templateName: template.name}
		});
	}

	private showDeleteTemplateConfirmation(template: EmailTemplateViewModel) {
		this.showDeleteTemplateModal = true;
		this.deleteTemplateModel = template;
	}

	private async deleteTemplate() {
		try {
			await emailApi.deleteEmailTemplate(this.currentWorkspace.id, this.deleteTemplateModel.templateId);
			this.loadTemplates();
		} catch (err) {
			Toast.showError(`Failed to delete template ${err}`);
		} finally {
			this.showDeleteTemplateModal = false;
		}
	}

	private canSave() {
		const invalid = this.$v.$invalid;
		const activeTemplateTouched = this.activeTemplateId !== this.lastActiveTemplateId;
		const domainTouched = this.$v.domain.$dirty;
		return (activeTemplateTouched || domainTouched) && !invalid;
	}

	private async save() {
		try {
			this.updateInProgress = true;
			const response: EmailWorkspaceModel = await emailApi.saveEmailWorkspace(this.currentWorkspace.id, {
				activeTemplateId: this.activeTemplateId,
				outboundEmailDomain: this.domain
			});
			this.setConfig(response);
			Toast.showSuccess(`Email configuration successfully saved`);
		} catch (err) {
			Toast.showError(`Failed to save email configuration ${err}`);
		} finally {
			this.updateInProgress = false;
		}
	}

	private async deleteDomain() {
		const response: EmailWorkspaceModel = await emailApi.saveEmailWorkspace(this.currentWorkspace.id, {
			activeTemplateId: this.savedEmailWorkspace.activeTemplateId,
			outboundEmailDomain: ''
		});

		Toast.showSuccess('Successfuly cleared company domain');
		this.setConfig(response);
	}

	private async verifyDomain() {
		try {
			this.verifyInProgress = true;
			const verifyResponse = await emailApi.verifyDomain(this.currentWorkspace.id);

			if (verifyResponse.success) {
				this.domainVerified = true;
				Toast.showSuccess(`Domain is now verified`);
			} else {
				Toast.showInfo(verifyResponse.errorMessage);
			}
		} catch (err) {
			Toast.showError(`Failed to verify domain. ${err.message}`);
		} finally {
			this.verifyInProgress = false;
		}
	}
}
